import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorState } from "./States";

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an unhandled error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="p-6 max-w-lg mx-auto">
          <ErrorState
            title="An unexpected error occurred"
            description={this.state.error?.message || "Something went wrong while rendering this page."}
            action={{
              label: "Try Again",
              onClick: this.handleReset,
            }}
            secondaryAction={{
              label: "Reload Page",
              onClick: () => window.location.reload(),
            }}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
